import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Businesscase, BusinesscaseRevenue, CostInputs, SubsidyInputs, BusinesscaseExtraCost } from '@/types/businesscase';
import { alOpgeslagen, opgeslagenWaarden } from '@/features/businesscases/data/opslaan';

interface Project {
  id: string;
  name: string;
  project_number: string | null;
}

export function useBusinesscase(businesscaseId: string) {
  const [businesscase, setBusinesscase] = useState<Businesscase | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [revenues, setRevenues] = useState<BusinesscaseRevenue[]>([]);
  const [extraCosts, setExtraCosts] = useState<BusinesscaseExtraCost[]>([]);
  const [loading, setLoading] = useState(true);
  /* Niet gevonden (ook: geen geldig id) of een andere fout; de pagina toont het in plaats van een leeg scherm. */
  const [fout, setFout] = useState<{ soort: 'niet-gevonden' | 'fout'; bericht: string } | null>(null);
  const { toast } = useToast();

  // Fetch businesscase and revenues
  const fetchData = useCallback(async () => {
    if (!businesscaseId) return;
    
    try {
      setLoading(true);
      setFout(null);

      // Fetch businesscase; het project los (geen embed: een ontbrekend project laat de case niet vallen)
      const { data: bcData, error: bcError } = await supabase
        .from('businesscases')
        .select('*')
        .eq('id', businesscaseId)
        .maybeSingle();

      if (bcError?.code === '22P02' || (!bcError && !bcData)) {
        setBusinesscase(null);
        setFout({ soort: 'niet-gevonden', bericht: 'Deze businesscase bestaat niet (meer), of je mag hem niet zien.' });
        return;
      }
      if (bcError) throw bcError;

      setBusinesscase(bcData as Businesscase);
      if (bcData.project_id) {
        const { data: p } = await supabase.from('projects').select('id, name, project_number').eq('id', bcData.project_id).maybeSingle();
        setProject((p as Project) ?? null);
      } else {
        setProject(null);
      }

      // Fetch revenues
      const { data: revData, error: revError } = await supabase
        .from('businesscase_revenues')
        .select('*')
        .eq('businesscase_id', bcData.id)
        .order('year');

      if (revError) throw revError;
      setRevenues(revData || []);
      
      // Fetch extra costs
      const { data: extraCostsData, error: extraCostsError } = await supabase
        .from('businesscase_extra_costs')
        .select('*')
        .eq('businesscase_id', bcData.id)
        .order('created_at');

      if (extraCostsError) throw extraCostsError;
      setExtraCosts(extraCostsData || []);
    } catch (error: any) {
      console.error('Error fetching businesscase:', error);
      setFout({ soort: 'fout', bericht: error?.message || 'Kon businesscase niet laden' });
    } finally {
      setLoading(false);
    }
  }, [businesscaseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Exposed refetch function for external triggers (e.g., Excel import)
  const refetchRevenues = useCallback(async () => {
    if (!businesscaseId) return;
    
    try {
      const { data, error } = await supabase
        .from('businesscase_revenues')
        .select('*')
        .eq('businesscase_id', businesscaseId)
        .order('year');

      if (error) throw error;
      setRevenues(data || []);
    } catch (error: any) {
      console.error('Error refetching revenues:', error);
    }
  }, [businesscaseId]);

  // Update costs
  const updateCosts = async (costs: CostInputs) => {
    if (!businesscase) return;

    try {
      const { error } = await supabase
        .from('businesscases')
        .update({
          battery_purchase_cost: costs.batteryPurchaseCost,
          installation_cost: costs.installationCost,
          software_setup_cost: costs.softwareSetupCost,
          annual_subscription: costs.annualSubscription,
          other_costs: costs.otherCosts,
        })
        .eq('id', businesscase.id);

      if (error) throw error;

      // Update local state with correct property names
      setBusinesscase(prev => prev ? { 
        ...prev,
        battery_purchase_cost: costs.batteryPurchaseCost,
        installation_cost: costs.installationCost,
        software_setup_cost: costs.softwareSetupCost,
        annual_subscription: costs.annualSubscription,
        other_costs: costs.otherCosts,
      } : null);
    } catch (error: any) {
      console.error('Error updating costs:', error);
      toast({
        title: 'Fout bij opslaan',
        description: error.message || 'Kon kosten niet opslaan',
        variant: 'destructive'
      });
      throw error;
    }
  };

  // Update subsidies
  const updateSubsidies = async (subsidies: SubsidyInputs) => {
    if (!businesscase) return;

    try {
      const { error } = await supabase
        .from('businesscases')
        .update({
          eia_enabled: subsidies.eiaEnabled,
          flex_e_enabled: subsidies.flexEEnabled,
          flex_e_value: subsidies.flexEValue,
          other_subsidy: subsidies.otherSubsidy,
          tax_rate: subsidies.taxRate,
        })
        .eq('id', businesscase.id);

      if (error) throw error;

      // Update local state
      setBusinesscase(prev => prev ? { 
        ...prev,
        eia_enabled: subsidies.eiaEnabled,
        flex_e_enabled: subsidies.flexEEnabled,
        flex_e_value: subsidies.flexEValue,
        other_subsidy: subsidies.otherSubsidy,
        tax_rate: subsidies.taxRate
      } : null);
    } catch (error: any) {
      console.error('Error updating subsidies:', error);
      toast({
        title: 'Fout bij opslaan',
        description: error.message || 'Kon subsidies niet opslaan',
        variant: 'destructive'
      });
      throw error;
    }
  };

  // Update single year revenue
  const updateYearRevenue = async (revenueId: string, data: Partial<BusinesscaseRevenue>) => {
    try {
      const { error } = await supabase
        .from('businesscase_revenues')
        .update(data)
        .eq('id', revenueId);

      if (error) throw error;

      // Update local state
      setRevenues(prev => prev.map(rev => 
        rev.id === revenueId ? { ...rev, ...data } : rev
      ));
    } catch (error: any) {
      console.error('Error updating revenue:', error);
      toast({
        title: 'Fout bij opslaan',
        description: error.message || 'Kon opbrengst niet opslaan',
        variant: 'destructive'
      });
      throw error;
    }
  };

  // Add new year
  const addYear = async (year: number) => {
    if (!businesscase) return;

    try {
      const { data, error } = await supabase
        .from('businesscase_revenues')
        .insert({
          businesscase_id: businesscase.id,
          year
        })
        .select()
        .single();

      if (error) throw error;

      setRevenues(prev => [...prev, data].sort((a, b) => a.year - b.year));
      
      toast({
        title: 'Jaar toegevoegd',
        description: `Jaar ${year} is toegevoegd`,
      });
    } catch (error: any) {
      console.error('Error adding year:', error);
      toast({
        title: 'Fout bij toevoegen',
        description: error.message || 'Kon jaar niet toevoegen',
        variant: 'destructive'
      });
    }
  };

  // Save calculated values back to database — alleen als er iets anders uitkomt (zie opslaan.ts)
  const saveCalculatedValues = useCallback(async (calculations: any) => {
    if (!businesscase) return;
    const waarden = opgeslagenWaarden(calculations);
    if (alOpgeslagen(businesscase as unknown as Record<string, unknown>, waarden)) return;

    try {
      const { error } = await supabase
        .from('businesscases')
        .update(waarden)
        .eq('id', businesscase.id);

      if (error) throw error;
      setBusinesscase(prev => prev ? { ...prev, ...waarden } as Businesscase : null);
    } catch (error: any) {
      console.error('Error saving calculated values:', error);
    }
  }, [businesscase]);

  // Update metadata (name, notes)
  const updateMetadata = async (data: { name?: string; notes?: string }) => {
    if (!businesscase) return;

    try {
      const { error } = await supabase
        .from('businesscases')
        .update(data)
        .eq('id', businesscase.id);

      if (error) throw error;

      setBusinesscase(prev => prev ? { ...prev, ...data } : null);
    } catch (error: any) {
      console.error('Error updating metadata:', error);
      toast({
        title: 'Fout bij opslaan',
        description: error.message || 'Kon gegevens niet opslaan',
        variant: 'destructive'
      });
      throw error;
    }
  };

  return {
    businesscase,
    project,
    revenues,
    extraCosts,
    loading,
    fout,
    updateCosts,
    updateSubsidies,
    updateYearRevenue,
    addYear,
    saveCalculatedValues,
    updateMetadata,
    refetch: fetchData,
    refetchRevenues
  };
}
